import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useToast } from '../hooks/use-toast';
import {
  Lightbulb, TrendingUp, TrendingDown, Clock, DollarSign,
  PenLine, Tag, BarChart3, Star, AlertCircle, Image, PlusCircle,
  RefreshCw, Sparkles, ChevronRight, Award, Loader2, Lock, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for insight categories
const INSIGHT_ICONS = {
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'lightbulb': Lightbulb,
  'clock': Clock,
  'dollar-sign': DollarSign,
  'pen-line': PenLine,
  'tag': Tag,
  'bar-chart': BarChart3,
  'star': Star,
  'alert-circle': AlertCircle,
  'image': Image,
  'plus-circle': PlusCircle,
  'award': Award
};

// Color mapping for insight categories
const CATEGORY_COLORS = {
  performance: 'bg-blue-100 text-blue-700 border-blue-200',
  optimization: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  timing: 'bg-green-100 text-green-700 border-green-200',
  pricing: 'bg-purple-100 text-purple-700 border-purple-200',
  content: 'bg-orange-100 text-orange-700 border-orange-200',
  trends: 'bg-indigo-100 text-indigo-700 border-indigo-200'
};

const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-gray-400'
};

// Helper to get icon component
const getIcon = (iconName) => {
  return INSIGHT_ICONS[iconName] || Lightbulb;
};

// InsightItem component moved outside
const InsightItem = ({ insight, compact = false, userTier }) => {
  const IconComponent = INSIGHT_ICONS[insight.icon] || Lightbulb;
  const categoryColor = CATEGORY_COLORS[insight.category] || CATEGORY_COLORS.optimization;
  const isLocked = insight.tier === 'advanced' && userTier !== 'premium';
  
  if (compact) {
    return (
      <div className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        isLocked ? 'bg-gray-50 opacity-75' : 'bg-white hover:border-gray-300'
      }`}>
        <div className={`p-2 rounded-lg ${categoryColor}`}>
          <IconComponent className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm truncate">{insight.title}</p>
            {isLocked && <Lock className="w-3 h-3 text-gray-400" />}
            {insight.tier === 'advanced' && !isLocked && (
              <Crown className="w-3 h-3 text-yellow-500" />
            )}
          </div>
          <p className="text-xs text-gray-500 line-clamp-2">{insight.message}</p>
        </div>
        {insight.metric && (
          <Badge variant="secondary" className="text-xs whitespace-nowrap">
            {insight.metric.value}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${
      isLocked 
        ? 'bg-gray-50 border-gray-200 opacity-75' 
        : `${categoryColor} border-opacity-50`
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`p-2.5 rounded-xl ${isLocked ? 'bg-gray-200' : 'bg-white/80'}`}>
            <IconComponent className={`w-5 h-5 ${isLocked ? 'text-gray-400' : ''}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold">{insight.title}</h4>
              <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[insight.priority]}`} />
              {isLocked && <Lock className="w-4 h-4 text-gray-400" />}
              {insight.tier === 'advanced' && !isLocked && (
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              )}
            </div>
            <p className="text-sm opacity-90">{insight.message}</p>
            
            {/* Tips list if available */}
            {insight.tips && insight.tips.length > 0 && (
              <ul className="mt-2 space-y-1">
                {insight.tips.map((tip, i) => (
                  <li key={i} className="text-xs flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-current" />
                    {tip}
                  </li>
                ))}
              </ul>
            )}
            
            {/* Action button if available */}
            {insight.action && !isLocked && (
              <Link to={insight.action.link}>
                <Button size="sm" variant="secondary" className="mt-3">
                  {insight.action.label}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
        </div>
        
        {/* Metric badge */}
        {insight.metric && (
          <div className="text-right">
            <p className="text-xs opacity-70">{insight.metric.label}</p>
            <p className="text-lg font-bold">{insight.metric.value}</p>
          </div>
        )}
      </div>
      
      {/* Locked overlay message */}
      {isLocked && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <Link to="/membership">
            <Button size="sm" variant="outline" className="w-full">
              <Crown className="w-4 h-4 mr-2 text-yellow-500" />
              Upgrade to Premium for this insight
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

const AIInsightsCard = ({ 
  variant = 'full', // 'full' for analytics page, 'summary' for dashboard
  maxInsights = null,
  showRefresh = true,
  className = ''
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = variant === 'summary' 
        ? `${API_URL}/api/analytics/ai-insights/summary`
        : `${API_URL}/api/analytics/ai-insights`;
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to load insights');
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [token, variant]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        toast({
          title: 'Insights refreshed',
          description: 'Your AI insights have been updated'
        });
      } else {
        toast({
          title: 'Refresh failed',
          description: 'Could not refresh insights',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Error refreshing insights:', err);
      toast({
        title: 'Error',
        description: 'Failed to refresh insights',
        variant: 'destructive'
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchInsights();
    }
  }, [token, fetchInsights]);

  if (!token) {
    return null;
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-gray-500">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={fetchInsights}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const insights = data?.insights || [];
  const displayInsights = maxInsights ? insights.slice(0, maxInsights) : insights;
  const hasMoreInsights = maxInsights && insights.length > maxInsights;

  // Summary variant (for Dashboard)
  if (variant === 'summary') {
    return (
      <Card className={`overflow-hidden ${className}`}>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-white">AI Insights</CardTitle>
                <CardDescription className="text-purple-100 text-xs">
                  Personalized tips to boost your sales
                </CardDescription>
              </div>
            </div>
            {showRefresh && (
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* AI Summary */}
          {data?.summary && (
            <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg mb-4 border border-purple-100">
              <p className="text-sm text-gray-700">{data.summary}</p>
            </div>
          )}
          
          {/* Insights list */}
          {displayInsights.length > 0 ? (
            <div className="space-y-3">
              {displayInsights.map((insight) => (
                <InsightItem key={insight.id} insight={insight} compact userTier={data?.user_tier} />
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Lightbulb className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Post ads to get personalized insights</p>
            </div>
          )}
          
          {/* View all link */}
          {(hasMoreInsights || insights.length > 0) && (
            <Link to="/analytics">
              <Button variant="ghost" className="w-full mt-4 text-purple-600 hover:text-purple-700">
                View All Insights
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
          
          {/* Membership upgrade prompt for free users */}
          {data?.user_tier === 'free' && (
            <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    Unlock {data?.total_insights - data?.available_insights} more insights
                  </p>
                  <p className="text-xs text-yellow-600">Upgrade to Premium for advanced tips</p>
                </div>
                <Link to="/membership">
                  <Button size="sm" className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white">
                    Upgrade
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full variant (for Analytics page)
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                AI Performance Insights
                <Badge variant="outline" className="font-normal">
                  {insights.length} tips
                </Badge>
              </CardTitle>
              <CardDescription>
                Personalized recommendations to improve your ad performance
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.from_cache && (
              <Badge variant="secondary" className="text-xs">
                Cached
              </Badge>
            )}
            <Badge variant="outline" className="capitalize">
              {data?.user_tier || 'free'} plan
            </Badge>
            {showRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* AI Summary */}
        {data?.summary && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl mb-6 border border-purple-100">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-purple-800 mb-1">AI Summary</p>
                <p className="text-sm text-gray-700">{data.summary}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Insights Grid */}
        {displayInsights.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayInsights.map((insight) => (
              <InsightItem key={insight.id} insight={insight} userTier={data?.user_tier} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No insights yet</p>
            <p className="text-sm mt-1">Post some ads to start receiving personalized tips</p>
            <Link to="/post-ad">
              <Button className="mt-4">Post Your First Ad</Button>
            </Link>
          </div>
        )}
        
        {/* Premium Upgrade CTA */}
        {data?.user_tier !== 'premium' && data?.total_insights > data?.available_insights && (
          <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-yellow-800">
                    Unlock {data.total_insights - data.available_insights} Premium Insights
                  </p>
                  <p className="text-sm text-yellow-600">
                    Get pricing analysis, competitor insights, and trend predictions
                  </p>
                </div>
              </div>
              <Link to="/membership">
                <Button className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white">
                  Upgrade to Premium
                </Button>
              </Link>
            </div>
          </div>
        )}
        
        {/* Last updated */}
        {data?.generated_at && (
          <p className="text-xs text-gray-400 text-center mt-4">
            Last updated: {new Date(data.generated_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AIInsightsCard;
